import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, Space, message, Card, Select, Pagination } from "antd";
import jsPDF from 'jspdf';
import { Row, Form, Col } from "antd";
import { DownloadOutlined, FilterOutlined, ClearOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
import dayjs from "dayjs";
import WasteApi from "../common/Apis/WasteApi";
import QuestionnairesApi from "../common/Apis/QuestionnairesApi";

const RawWasteReports = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [WasteReport, setWasteReport] = React.useState([]);
    const [filteredReport, setFilteredReport] = React.useState([]);
    const [TotalQuantity, setTotalQuantity] = React.useState();
    const [productionCoast, setproductionCoast] = React.useState();
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
    const CURRENCY = decodedToken.currency === "USD" ? "$" : "€";

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    
    // Filter states
    const [productCodeFilter, setProductCodeFilter] = useState(null);
    const [wastedByFilter, setWastedByFilter] = useState(null);

    // Filter options
    const [productCodeOptions, setProductCodeOptions] = useState([]);
    const [wastedByOptions, setWastedByOptions] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique filter options from data
    const extractFilterOptions = (data) => {
        const productCodes = new Set();
        const wastedBy = new Set();

        data.forEach((item) => {
            if (item?.productDTO?.productCode) {
                productCodes.add(item.productDTO.productCode);
            }
            if (item?.createdBy) {
                wastedBy.add(item.createdBy);
            }
        });

        setProductCodeOptions(Array.from(productCodes).sort());
        setWastedByOptions(Array.from(wastedBy).sort());
    };

    // Apply filters to data - FIXED: This now properly filters the data
    const applyFilters = useCallback((data) => {
        if (!data || data.length === 0) {
            setFilteredReport([]);
            return;
        }

        let filtered = [...data]; // Create a copy of the data

        // Product Code filter
        if (productCodeFilter) {
            filtered = filtered.filter(item => 
                item?.productDTO?.productCode === productCodeFilter
            );
        }

        // Wasted By filter
        if (wastedByFilter) {
            filtered = filtered.filter(item => 
                item?.createdBy === wastedByFilter
            );
        }

        setFilteredReport(filtered);

        setPagination(prev => ({
            ...prev,
            current: 1,
            total: filtered.length,
        }));
    }, [productCodeFilter, wastedByFilter]);

    // Reset filters - FIXED: Resets both filter values and the filtered report
    const resetFilters = () => {
        setProductCodeFilter(null);
        setWastedByFilter(null);
        // After resetting filters, the useEffect will trigger and apply filters with null values
        // which will show all data
    };

    // Apply filters whenever filter values or WasteReport changes
    useEffect(() => {
        if (WasteReport.length > 0) {
            applyFilters(WasteReport);
        } else {
            setFilteredReport([]);
        }
    }, [WasteReport, productCodeFilter, wastedByFilter, applyFilters]);

    const handlePrintButtonClick2 = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const fontSize = 12;
        const margin = 5;
        const cellWidthFactor = 0.295;
        const cellHeightFactor = 0.2;

        doc.setFontSize(fontSize);

        const table = document.querySelector('.table');
        const rows = table.querySelectorAll('tr');

        const pageTitle = 'Raw Waste Reports';
        const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
        const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
        doc.text(pageTitleXPos, margin, pageTitle);

        doc.setFontSize(fontSize);
        const currentDate = new Date().toLocaleString();
        const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
        const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
        doc.text(dateTimeXPos, margin, currentDate);

        let yPos = margin + fontSize;

        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            let xPos = margin;

            cells.forEach((cell, cellIndex) => {
                const cellText = cell.textContent.trim();
                const cellWidth = cell.offsetWidth * cellWidthFactor;
                const cellHeight = row.offsetHeight * cellHeightFactor;

                if (yPos + cellHeight > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    yPos = margin + fontSize;
                    const pageTitle = 'Waste Reports';
                    const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
                    const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
                    doc.text(pageTitleXPos, margin, pageTitle);

                    doc.setFontSize(fontSize);
                    const currentDate = new Date().toLocaleString();
                    const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
                    const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
                    doc.text(dateTimeXPos, margin, currentDate);
                }

                const isHeader = cell.tagName.toLowerCase() === 'th';
                const fontWeight = isHeader ? 'bold' : 'normal';

                doc.setFont(undefined, fontWeight);

                const textWidth = doc.getStringUnitWidth(cellText) * fontSize / doc.internal.scaleFactor;
                const cellPadding = 2;
                let xPosAlign = xPos + (cellWidth - textWidth - cellPadding) / 2;

                if (!isHeader) {
                    xPosAlign = xPos;
                }
                doc.rect(xPos, yPos, cellWidth, cellHeight);
                doc.text(xPosAlign + 1, yPos + 5, cellText);

                xPos += cellWidth;
            });

            yPos += row.offsetHeight * cellHeightFactor;
        });

        window.open(doc.output('bloburl'), '_blank');
    };


    const handleExportToExcel = () => {
        const displayData = [...(filteredReport.length > 0 ? filteredReport : WasteReport)]
    .sort((a, b) => Number(b.createdDate) - Number(a.createdDate));
        
        if (!displayData || displayData.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);
        
        try {
            const excelData = paginatedData.map((item, index) => ({
                'S.No': index + 1,
                'Date': formatDate(item.createdDate),
                'Product Code': item?.productDTO?.productCode || '-',
                'Product Description': item?.productName || '-',
                'Product Qty': `${(() => {
                    const value = Number(item?.quantity || 0);

                    const normalStr = value.toLocaleString("fullwide", {
                        useGrouping: false,
                        maximumFractionDigits: 20,
                    });

                    const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                    return match ? match[0] : "0";
                })()} ${item?.unit || ''}`,
                'Wasted By': item?.createdBy || '-',
                'Wasted Qty': `${(() => {
                    const value = Number(item?.quantity || 0);

                    const normalStr = value.toLocaleString("fullwide", {
                        useGrouping: false,
                        maximumFractionDigits: 20,
                    });

                    const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                    return match ? match[0] : "0";
                })()} ${item?.unit || ''}`,
                'Cost': `${CURRENCY || ''} ${CurrencyConvert(parseFloat(item?.productDTO?.costPriceUoM2) * parseFloat(item?.quantity))}`
            }));

            const filteredTotalCost = displayData.reduce((sum, item) => {
                const cost = parseFloat(item?.productDTO?.costPriceUoM2) * parseFloat(item?.quantity);
                return sum + (isNaN(cost) ? 0 : cost);
            }, 0);
            
            excelData.push({
                'S.No': '',
                'Date': '',
                'Product Code': '',
                'Product Description': '',
                'Product Qty': '',
                'Wasted By': '',
                'Wasted Qty': '',
                'Cost': ''
            });
        
            excelData.push({
                'S.No': '',
                'Date': '',
                'Product Code': '',
                'Product Description': 'TOTAL COST',
                'Product Qty': '',
                'Wasted By': '',
                'Wasted Qty': '',
                'Cost': `${CURRENCY} ${CurrencyConvert(filteredTotalCost)}`
            });

            const ws = XLSX.utils.json_to_sheet(excelData);

            const colWidths = [
                { wch: 8 },   // S.No
                { wch: 15 },  // Date
                { wch: 15 },  // Product Code
                { wch: 30 },  // Product Description
                { wch: 12 },  // Product Qty
                { wch: 15 },  // Wasted By
                { wch: 12 },  // Wasted Qty
                { wch: 15 }   // Cost
            ];
            ws['!cols'] = colWidths;

            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const address = XLSX.utils.encode_col(C) + '1';
                if (!ws[address]) continue;
                ws[address].s = {
                    font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
                    fill: { fgColor: { rgb: "4F81BD" }, patternType: "solid" },
                    alignment: { horizontal: "center", vertical: "center" }
                };
            }

            const lastRow = excelData.length;
            for (let i = lastRow; i <= lastRow; i++) {
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const address = XLSX.utils.encode_col(C) + i;
                    if (!ws[address]) continue;
                    ws[address].s = {
                        font: { bold: true, sz: 11 },
                        fill: { fgColor: { rgb: "FFC000" }, patternType: "solid" },
                        alignment: { horizontal: "center", vertical: "center" }
                    };
                }
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Raw Waste Report");

            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');
            let fileName = `Raw_Waste_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
            
            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                fileName = `Raw_Waste_Report_${startStr}_to_${endStr}.xlsx`;
            }

            XLSX.writeFile(wb, fileName);
            
            message.success("Report exported successfully!");
        } catch (error) {
            console.error("Error exporting report:", error);
            message.error("Failed to export report");
        } finally {
            setExportLoading(false);
        }
    };

    const getCurrent = useCallback(() => {
        const todayDate = dayjs().format('DD/MM/YYYY');
        const tomorrowDate = dayjs().add(1, 'day').format('DD/MM/YYYY');

        formAdd.setFieldsValue({
            startDate: dayjs(todayDate, 'DD/MM/YYYY'),
            endDate: dayjs(tomorrowDate, 'DD/MM/YYYY'),
        });

        WasteApi.ListproductRawWasteReport()
            .then((response) => {
                PageLoading();
                setWasteReport(response?.payloadDto || []);

                setPagination({
                    current: (response.number || 0) + 1,
                    pageSize: response.size || 10,
                    total: response.totalElements || 0,
                });
                
                if (response?.payloadDto) {
                    extractFilterOptions(response.payloadDto);
                }
                
                const totalQuantity = response?.payloadDto.reduce((total, item) => total + (item?.quantity || 0), 0);
                setTotalQuantity(totalQuantity);

                const productionCoast = response?.payloadDto.reduce((total, item) => total + (parseFloat(item?.productionCoast) || 0), 0);
                setproductionCoast(productionCoast);
            })
            .catch((err) => {
                console.log(err);
                if (err.apiError?.status) {
                    notification.open({
                        message: (
                            <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                        ),
                        description: err.apiError.message,
                        closeIcon: false,
                        duration: 3,
                        style: {
                            backgroundColor: "#fff2f0",
                            color: "red",
                        },
                    });
                }
            });

    }, []);

    useEffect(() => {
        PageLoading();
        getCurrent();
    }, [PageLoading, getCurrent]);

    const formRef = useRef(null);

    const handleSubmit = (data) => {
        setFilterLoading(true);
        const startDateN = dayjs(data.startDate).format("DD/MM/YYYY");
        const endDateN = dayjs(data.endDate).format("DD/MM/YYYY");

        let singleLineString = `createdDate=BETWEEN${convertToUnixTimestamp(startDateN)}AND${convertToUnixTimestamp(endDateN)}&subTenant.id=${decodedToken?.subTenantId}`;
        let filderData = window.btoa(
            "componentCode=PRODUCT_WASTE&subTenant.id=" + decodedToken?.subTenantId + "&" + singleLineString
        );

        QuestionnairesApi.FilterQuestionsetNext(filderData, 0, pagination.pageSize)
            .then((response) => {
                
                const filteredPayload = (response.payloadDto || []).filter(
                item => item?.production === false
            );
                setWasteReport(filteredPayload);
                
                setPagination(prev => ({
                    ...prev,
                    current: 1,
                    total: filteredPayload.length,
                }));

                if (response?.payloadDto) {
                    extractFilterOptions(response.payloadDto);
                }
                
                const totalQuantity = response?.payloadDto.reduce((total, item) => total + (item?.quantity || 0), 0);
                setTotalQuantity(totalQuantity);
                const productionCoast = response?.payloadDto.reduce((total, item) => total + (parseFloat(item?.productionCoast) || 0), 0);
                setproductionCoast(productionCoast);
                PageLoading();
                setFilterLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setFilterLoading(false);
                if (err.apiError?.status) {
                    notification.open({
                        message: (
                            <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                        ),
                        description: err.apiError.message,
                        closeIcon: false,
                        duration: 3,
                        style: {
                            backgroundColor: "#fff2f0",
                            color: "red",
                        },
                    });
                }
            });
    }

    function formatDate(date) {
        if (!date) return '-';
        const dateNew = parseInt(date, 10);
        const options = { day: "2-digit", month: "short", year: "numeric" };
        const formattedDate = new Date(dateNew).toLocaleDateString(
            "en-US",
            options
        );

        const [month, day, year] = formattedDate.split(" ");
        const capitalizedMonth = month.toUpperCase();
        return `${day} ${capitalizedMonth} ${year}`;
    }

    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    const handleClear = () => {
        setIsLoading(true);
        setproductionCoast(null);
        setTotalQuantity(null);
        setWasteReport([]);
        setFilteredReport([]);
        // Reset filters
        setProductCodeFilter(null);
        setWastedByFilter(null);
        setTimeout(() => {
            getCurrent();
            setIsLoading(false);
        }, 800);
    };

    const CurrencyConvert = (value) => {
        const formattedNumber = new Intl.NumberFormat('en-US').format(value);
        return formattedNumber;
    };

    const calculateTotalCost = () => {
        const displayData = filteredReport.length > 0 ? filteredReport : WasteReport;
        return displayData.reduce((total, date) => {
            const cost = parseFloat(date?.productDTO?.costPriceUoM2) * parseFloat(date?.quantity);
            return total + (isNaN(cost) ? 0 : cost);
        }, 0);
    };

    // Determine which data to display
    const displayData = [...(filteredReport.length > 0 ? filteredReport : WasteReport)]
    .sort((a, b) => Number(b.createdDate) - Number(a.createdDate));

    
    const paginatedData = displayData.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
);

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Filter Options</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="raw_waste_filter_form"
                                layout="vertical"
                                onFinish={handleSubmit}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Apply Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Clear
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* Advanced Filters Section - Product Code & Wasted By */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                background: '#fafafa'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <SearchOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Advanced Filters</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        (Filter by Product Code or Wasted By)
                                    </span>
                                </Space>
                            </div>
                            
                            <Row gutter={[16, 16]}>
                                <Col lg={6} md={12} sm={24} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Product Code
                                    </div>
                                    <Select
                                        placeholder="All Product Codes"
                                        style={{ width: '100%' }}
                                        value={productCodeFilter}
                                        onChange={setProductCodeFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {productCodeOptions.map(productCode => (
                                            <Select.Option key={productCode} value={productCode}>{productCode}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                                
                                <Col lg={6} md={12} sm={24} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Wasted By
                                    </div>
                                    <Select
                                        placeholder="All Users"
                                        style={{ width: '100%' }}
                                        value={wastedByFilter}
                                        onChange={setWastedByFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {wastedByOptions.map(user => (
                                            <Select.Option key={user} value={user}>{user}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                                
                                <Col lg={12} md={24} sm={24} xs={24} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <Button
                                        onClick={resetFilters}
                                        icon={<ClearOutlined />}
                                        style={{ height: '32px' }}
                                    >
                                        Reset Filters
                                    </Button>
                                </Col>
                            </Row>
                            
                            <div style={{ marginTop: '12px', padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px', color: '#1890ff' }}>
                                Showing {displayData.length} of {WasteReport.length} records
                                {(productCodeFilter || wastedByFilter) && 
                                    ` (Filtered: ${productCodeFilter ? `Product Code: ${productCodeFilter}` : ''}${wastedByFilter ? `, Wasted By: ${wastedByFilter}` : ''})`
                                }
                            </div>
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Raw Waste Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {displayData.length > 0 && `(${displayData.length} records found)`}
                                    </span>
                                </Space>
                                <Button
                                    type="primary"
                                    onClick={handleExportToExcel}
                                    loading={exportLoading}
                                    icon={<DownloadOutlined />}
                                    style={{ 
                                        height: '36px',
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a'
                                    }}
                                >
                                    Export Raw Waste Report
                                </Button>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <table className="table users-table Reportable" style={{ width: '100%', minWidth: '800px' }}>
                                    <thead>
                                        <tr>
                                            <th colSpan="7" className="text-center" style={{ fontSize: '16px', padding: '12px' }}>
                                                Raw Waste Product
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Date</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Product Code</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Product Description</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Product Qty</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Reason</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Wasted By</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }} className="text-end">Wasted Qty</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }} className="text-end">Cost</th>
                                        </tr>

                                        {displayData.length > 0 ? (
                                            <>
                                                {paginatedData.map((date, index) => (
                                                    <tr key={index}>
                                                        <td style={{ width: '13%', padding: '10px' }} className="CleanTable text-start text-center">
                                                            {formatDate(date.createdDate)}
                                                        </td>
                                                        <td style={{ width: '20%', padding: '10px' }} className="CleanTable text-start text-center">
                                                            <div style={{
                                                                overflowX: 'auto',
                                                                overflowY: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                maxWidth: '200px',
                                                                marginBottom: '10px',
                                                                marginTop: '-3px',
                                                                padding: '0px 0px 20px',
                                                                display: 'block',
                                                                }}>
                                                                {date?.productDTO?.productCode || '-'} 
                                                                </div>  
                                                        </td>
                                                        <td style={{ width: '20%', padding: '10px' }} className="CleanTable text-start text-center">
                                                            {date?.productName || '-'}
                                                        </td>
                                                        <td style={{ width: '10%', padding: '10px' }} className="CleanTable text-start text-center">
                                                            {(() => {
                                                                const value = Number(date?.quantity || 0);

                                                                const normalStr = value.toLocaleString("fullwide", {
                                                                    useGrouping: false,
                                                                    maximumFractionDigits: 20,
                                                                });

                                                                const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                                                                return match ? match[0] : "0";
                                                            })()} {date?.unit || ''}
                                                        </td>
                                                        <td style={{ width: '10%', padding: '10px' }} className="CleanTable text-start text-center">
                                                            {date.reason || '-'}
                                                        </td>
                                                        <td style={{ width: '10%', padding: '10px' }} className="CleanTable text-start text-center">
                                                            {date.createdBy || '-'}
                                                        </td>
                                                        <td style={{ width: '10%', padding: '10px' }} className="CleanTable text-end text-center">
                                                            {(() => {
                                                                const value = Number(date?.quantity || 0);

                                                                const normalStr = value.toLocaleString("fullwide", {
                                                                    useGrouping: false,
                                                                    maximumFractionDigits: 20,
                                                                });

                                                                const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);

                                                                return match ? match[0] : "0";
                                                            })()} {date?.unit || ''}
                                                        </td>
                                                        <td style={{ width: '10%', padding: '10px' }} className="CleanTable text-end text-center">
                                                            {CURRENCY || ''} {CurrencyConvert(parseFloat(date?.productDTO?.costPriceUoM2 || 0) * parseFloat(date?.quantity || 0))}
                                                        </td>
                                                    </tr>
                                                ))}
                                             
                                                <tr style={{ backgroundColor: '#f0f2f5', fontWeight: 'bold' }}>
                                                    <td colSpan="6" className="text-end text-center" style={{ fontSize: '14px', fontWeight: 'bold', padding: '12px' }}>
                                                        Total Cost:
                                                    </td>
                                                    <td className="text-end text-center" style={{ fontSize: '14px', fontWeight: 'bold', padding: '12px' }}>
                                                        {CURRENCY || ''} {CurrencyConvert(calculateTotalCost())}
                                                    </td>
                                                 </tr>
                                            </>
                                        ) : (
                                            <tr>
                                                <td colSpan={7} className="text-center" style={{ padding: '40px' }}>
                                                    <img
                                                        src="/assest/Images/emptyIcon.svg"
                                                        className="logo-png mt-3"
                                                        style={{ width: "10%" }}
                                                        alt="No data"
                                                    />
                                                    <br />
                                                    {filterLoading ? "Loading..." : "No data available"}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                <div style={{ marginTop: 16, textAlign: "right" }}>
                                <Pagination
                                    current={pagination.current}
                                    pageSize={pagination.pageSize}
                                    total={displayData.length}
                                    showSizeChanger
                                    showQuickJumper
                                    pageSizeOptions={["10", "20", "50", "100"]}
                                    showTotal={(total) => `Total ${total} items`}
                                    onChange={(page, pageSize) => {
                                        setPagination({
                                            ...pagination,
                                            current: page,
                                            pageSize,
                                        });
                                    }}
                                    onShowSizeChange={(current, size) => {
                                        setPagination({
                                            current: 1,
                                            pageSize: size,
                                            total: displayData.length,
                                        });
                                    }}
                                />
                            </div>
                            </div>
                        </Card>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default RawWasteReports;